import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  FaCode, FaPalette, FaSearch, FaBox, FaWordpress,
  FaShoppingCart, FaArrowRight, FaCheckCircle, FaRocket
} from 'react-icons/fa';
import './Services.css';

const fadeUp = {
  hidden: { opacity: 0, y: 34 },
  visible: { opacity: 1, y: 0 },
};

const services = [
  {
    icon: <FaPalette />,
    title: 'Website Design',
    desc: 'Modern UI/UX website designs with premium layout, clean spacing and strong brand presentation.',
    count: '20+ Projects',
    features: ['Custom UI/UX Design', 'Figma / Canva Design', 'Landing Pages', 'Responsive Layouts'],
  },
  {
    icon: <FaCode />,
    title: 'Web Development',
    desc: 'Fast, responsive and SEO-ready websites using React, HTML, CSS, JavaScript and modern tools.',
    count: '15+ Projects',
    features: ['React JS Development', 'Custom Websites', 'API Integration', 'Business Websites'],
  },
  {
    icon: <FaSearch />,
    title: 'SEO Services',
    desc: 'SEO setup to improve Google visibility, website structure, speed and local business ranking.',
    count: '8+ Projects',
    features: ['On-Page SEO', 'Technical SEO', 'Local SEO Mumbai', 'Meta Tags Setup'],
  },
  {
    icon: <FaBox />,
    title: 'Packaging Design',
    desc: 'Creative packaging, labels and product design concepts that help brands look professional.',
    count: '25+ Projects',
    features: ['Box Design', 'Label Design', 'Brand Packaging', 'Print Ready Files'],
  },
  {
    icon: <FaWordpress />,
    title: 'WordPress Development',
    desc: 'Professional WordPress websites with custom design, performance optimization and security.',
    count: '30+ Projects',
    features: ['Custom WordPress Website', 'WooCommerce', 'Speed Optimization', 'Security Setup'],
  },
  {
    icon: <FaShoppingCart />,
    title: 'E-Commerce Solutions',
    desc: 'Online stores with product pages, payment gateway, cart flow and conversion-focused design.',
    count: '12+ Projects',
    features: ['WooCommerce Store', 'Payment Gateway', 'Product Catalog', 'Order Flow'],
  },
];

const process = [
  { step: '01', title: 'Discovery', desc: 'Understanding your goals, target audience and website requirements.' },
  { step: '02', title: 'Planning', desc: 'Creating project structure, pages, content flow and clear timeline.' },
  { step: '03', title: 'Design', desc: 'Designing premium UI layouts with mobile-friendly experience.' },
  { step: '04', title: 'Development', desc: 'Building the website with clean, optimized and scalable code.' },
  { step: '05', title: 'Testing', desc: 'Checking responsiveness, speed, browser compatibility and forms.' },
  { step: '06', title: 'Launch', desc: 'Final deployment with support, SEO basics and performance checks.' },
];

export default function Services() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Amresh Prajapati Web Design & Development Services",
    "url": "https://freelanceramreshprajapatimumbai.in/services",
    "areaServed": "Mumbai, Virar, India",
    "serviceType": [
      "Website Design",
      "Web Development",
      "SEO Services",
      "WordPress Development",
      "React Development",
      "E-Commerce Development"
    ]
  };

  return (
    <>
      <Helmet>
        <title>Web Design, Development & SEO Services | Amresh Prajapati</title>
        <meta
          name="description"
          content="Website design, development, SEO, WordPress, React JS, ecommerce & packaging design services in Mumbai, Virar & Vasai. 4+ years experience, 70+ projects. Affordable pricing & fast delivery."
        />
        <meta
          name="keywords"
          content="website design services Mumbai, web development services Mumbai, SEO services Mumbai, WordPress developer Mumbai, React developer Mumbai, freelance web developer Virar, ecommerce website developer Mumbai, packaging design services"
        />
        <link rel="canonical" href="https://freelanceramreshprajapatimumbai.in/services" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <section className="servicesHero">
        <div className="servicesHero__orb servicesHero__orb--one" />
        <div className="servicesHero__orb servicesHero__orb--two" />

        <div className="servicesContainer servicesHero__inner">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.65 }}
          >
            <span className="servicesTag"><FaRocket /> Services</span>

            <h1>
              Premium Web Services That Help Your Brand <span>Grow Online</span>
            </h1>

            <p>
              I provide website design, web development, WordPress, SEO, e-commerce
              and creative digital solutions for businesses, startups and brands.
            </p>

            <div className="servicesHero__buttons">
              <Link to="/contact" className="servicesBtn servicesBtn--primary">
                Start Project <FaArrowRight />
              </Link>
              <Link to="/projects" className="servicesBtn servicesBtn--outline">
                View Work
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="servicesSection">
        <div className="servicesContainer">
          <motion.div
            className="servicesHead"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <span className="servicesTag"><FaCode /> What I Do</span>
            <h2>Complete Digital Services For <span>Your Business</span></h2>
            <p>Clean design, strong development and SEO-focused structure for better online presence.</p>
          </motion.div>

          <div className="services-detailed-grid">
            {services.map((svc, i) => (
              <motion.div
                className="service-detailed-card"
                key={svc.title}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: i * 0.06, duration: 0.55 }}
              >
                <div className="service-detailed-card__icon">{svc.icon}</div>
                <span className="service-count">{svc.count}</span>
                <h3>{svc.title}</h3>
                <p>{svc.desc}</p>

                <ul className="service-features">
                  {svc.features.map((item) => (
                    <li key={item}><FaCheckCircle /> {item}</li>
                  ))}
                </ul>

                <Link to="/contact" className="service-link">
                  Get Started <FaArrowRight />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="servicesSection process-section">
        <div className="servicesContainer">
          <motion.div
            className="servicesHead"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <span className="servicesTag"><FaRocket /> How I Work</span>
            <h2>Simple Process, <span>Professional Result</span></h2>
          </motion.div>

          <div className="process-grid">
            {process.map((item, i) => (
              <motion.div
                className="process-card"
                key={item.step}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
              >
                <span className="process-step">{item.step}</span>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="servicesCTA">
        <div className="servicesContainer">
          <motion.div
            className="servicesCTA__box"
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="servicesTag"><FaRocket /> Start Your Project</span>
            <h2>Need A Website Or Digital Solution? <span>Let’s Build It</span></h2>
            <p>I can help you create a modern website, business platform, SEO-ready page or custom web solution.</p>

            <Link to="/contact" className="servicesBtn servicesBtn--primary">
              Contact Me Now <FaArrowRight />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}